import { css } from '@compiled/react';
import { defineMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { MotionContainer } from './components/motion-container';
import { RovoSection } from './components/rovo-section';
import { GlobalNavDropdownLinkColumn } from './layouts/global-nav-dropdown-link-column';
import { GlobalNavEyebrow, useGlobalNavContext, DivOnHoverExit } from './nav-links-container';

function useNewSolutionLinks() {
	return {
		byUseCase: {
			id: 'byUseCase',
			title: defineMessage({
				id: 'globalnavv2.subnav.teams.links.column.by-use-case',
				defaultMessage: 'By Use Case',
			}),
			items: [
				{
					id: 'team-collaboration',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.team-collaboration',
						defaultMessage: 'Team collaboration',
					}),
					href: useLocalizedHref('/collections/teamwork'),
				},
				{
					id: 'strategy-and-planning',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.strategy-and-planning',
						defaultMessage: 'Strategy and planning',
					}),
					href: useLocalizedHref('/collections/strategy'),
				},
				{
					id: 'service-management',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.service-management',
						defaultMessage: 'Service management',
					}),
					href: useLocalizedHref('/collections/service'),
				},
				{
					id: 'software-development',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.software-development',
						defaultMessage: 'Software development',
					}),
					href: useLocalizedHref('/software/jira/dev'),
				},
			],
		},
		teamtype: {
			id: 'teamtype',
			title: defineMessage({
				id: 'globalnavv2.subnav.teams.links.column.teamtype',
				defaultMessage: 'By Team',
			}),
			items: [
				{
					id: 'software',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.software',
						defaultMessage: 'Software',
					}),
					href: useLocalizedHref('/software/jira/dev'),
				},
				{
					id: 'marketing',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.marketing',
						defaultMessage: 'Marketing',
					}),
					href: useLocalizedHref('/teams/marketing'),
				},
				{
					id: 'it',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.it',
						defaultMessage: 'IT',
					}),
					href: useLocalizedHref('/teams/it'),
				},
				{
					id: 'product-discovery-by-team',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.product-discovery-by-team',
						defaultMessage: 'Product',
					}),
					href: useLocalizedHref('/software/jira/product-discovery '),
				},
			],
		},
		byTeamSize: {
			id: 'byTeamSize',
			title: defineMessage({
				id: 'globalnavv2.subnav.teams.links.column.by-team-size',
				defaultMessage: 'By Size',
			}),
			items: [
				{
					id: 'enterprise',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.enterprise',
						defaultMessage: 'Enterprise',
					}),
					href: useLocalizedHref('/enterprise'),
				},
				{
					id: 'small-business',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.small-business',
						defaultMessage: 'Small Business',
					}),
					href: useLocalizedHref('/software/small-business'),
				},
				{
					id: 'startup',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.startup',
						defaultMessage: 'Startup',
					}),
					href: useLocalizedHref('/software/startups'),
				},
				{
					id: 'non-profit',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.non-profit',
						defaultMessage: 'Non-profit',
					}),
					href: useLocalizedHref('/teams/nonprofits'),
				},
			],
		},
		byIndustry: {
			id: 'byIndustry',
			title: defineMessage({
				id: 'globalnavv2.subnav.teams.links.column.by-industry',
				defaultMessage: 'By Industry',
			}),
			items: [
				{
					id: 'retail',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.retail',
						defaultMessage: 'Retail',
					}),
					href: useLocalizedHref('/industries/retail'),
				},
				{
					id: 'telecommunications',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.telecommunications',
						defaultMessage: 'Telecommunications',
					}),
					href: useLocalizedHref('/industries/telecom'),
				},
				{
					id: 'professional-services',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.professional-services',
						defaultMessage: 'Professional Services',
					}),
					href: useLocalizedHref('/industries/professional-services'),
				},
				{
					id: 'government',
					text: defineMessage({
						id: 'globalnavv2.subnav.teams.links.government',
						defaultMessage: 'Government',
					}),
					href: useLocalizedHref('/government'),
				},
			],
		},
	} as const;
}

export const GlobalNavSolutionsDropdown = ({
	productKey = 'home',
}: {
	productKey?: ProductKeyTypes;
}) => {
	const intl = useIntl();
	const { direction } = useGlobalNavContext();

	const newSolutionsLinks = useNewSolutionLinks();
	const newGlobalNavDropdownLinkColumns = Object.values(newSolutionsLinks);

	return (
		<DivOnHoverExit>
			<TypographyStyles>
				<div css={SolutionsContainerCSS}>
					<div css={CollectionsMobileLabelCSS} className="mobile-label">
						{intl.formatMessage({
							id: 'GlobalNav.collections.solutions.mobilelabel',
							defaultMessage: 'Solutions',
						})}
					</div>
					<MotionContainer direction={direction}>
						<div className="new-solutions-bottom" css={CollectionsSolutionsBottomCSS}>
							{newGlobalNavDropdownLinkColumns.map((column) => {
								return (
									<GlobalNavDropdownLinkColumn key={column.id}>
										<GlobalNavEyebrow removeMargin={true}>
											{intl.formatMessage(column.title)}
										</GlobalNavEyebrow>

										<div
											css={css({
												display: 'grid',
												gridTemplateRows: 'auto',
											})}
										>
											<ul
												css={css({
													listStyleType: 'none',
													margin: '0',
													padding: '5px 0 0 0',
													[media.below.md]: {
														paddingBlockEnd: '15px',
													},
												})}
											>
												{column.items.map((link) => {
													return (
														<li
															key={link.id}
															css={css({
																listStyleType: 'none',
																margin: '0',
																padding: '8px 0',
															})}
														>
															<div
																key={link.id}
																css={css({
																	// nav doesn't follow the typography
																	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
																	a: {
																		fontWeight: 400,
																		fontSize: '0.875rem',
																		color: wacToken('color.primary.black'),
																		'&:visited': {
																			textDecoration: 'none',
																			color: wacToken('color.primary.black'),
																		},
																		'&:hover, &:focus, &:visited:hover, &:visited:focus': {
																			color: wacToken('color.primary.blue'),
																		},
																	},

																	display: 'flex',
																	alignItems: 'end',
																})}
															>
																<Link
																	type="anchor"
																	href={link.href}
																	text={intl.formatMessage(link.text)}
																	aria-label={intl.formatMessage(link.text)}
																	ariaLabel={intl.formatMessage(link.text)}
																	dataEventProps={{
																		'data-event': 'clicked',
																		'data-event-component': 'linkButton',
																		'data-event-container': 'globalNavSubnav',
																		'data-product-key': productKey,
																	}}
																/>
															</div>
														</li>
													);
												})}
											</ul>
										</div>
									</GlobalNavDropdownLinkColumn>
								);
							})}
						</div>
						<RovoSection />
					</MotionContainer>
				</div>
			</TypographyStyles>
		</DivOnHoverExit>
	);
};

const SolutionsContainerCSS = css({
	[media.below.md]: {
		padding: 0,
	},
	[media.only.sm]: {
		width: '330px',
	},
});

const CollectionsMobileLabelCSS = css({
	display: 'none',
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '0.75rem',
	textTransform: 'uppercase',
	margin: `${token('space.300')} 0`,
	[media.below.md]: {
		display: 'block',
	},
});

const CollectionsSolutionsBottomCSS = css({
	backgroundColor: wacToken('color.neutral.light100'),
	display: 'grid',
	gridTemplateColumns: '1fr 1fr 1fr 1fr',
	gap: token('space.300'),
	paddingInline: token('space.500'),
	paddingBlock: token('space.500'),
	[media.below.md]: {
		paddingBlockStart: token('space.0'),
		paddingInline: token('space.0'),
		gridTemplateColumns: '1fr',
	},
	[media.below.sm]: {
		paddingBlockEnd: token('space.400'),
	},
	[`&.new-solutions-bottom`]: {
		backgroundColor: wacToken('color.neutral.light0'),
	},
});
